/**
 * Product-agnostic SolShare software update decisions.
 * Uses solshareVersion (+ opaque component status for errors / in-progress).
 * Never branches on MK1/MK2 or component slug names.
 */
import semver from 'semver';

export type OtaComponentStatus = {
  state: string;
  timestamp?: number;
  version?: string;
  ttl?: number;
};

export type OtaComponent = {
  slug: string;
  reported: string | null;
  target: string | null;
  status: OtaComponentStatus | null;
};

export type SolshareVersionState = {
  reported: string | null;
  target: string | null;
  desired: string | null;
  minimum: string | null;
};

export type SoftwareOtaState = {
  components: OtaComponent[];
  solshareVersion: SolshareVersionState;
};

export type IdleLoopAction =
  | { type: 'done' }
  | { type: 'fail'; message: string }
  | { type: 'poll' }
  | { type: 'post'; version: string };

export type SoftwareUpdateRequirement = {
  needSoftwareUpdate: boolean;
  forceUpdate: boolean;
};

const ERROR_STATES = new Set(['ERROR', 'error', 'FAILED', 'failed']);
const TERMINAL_OK_STATES = new Set(['DONE', 'done', 'SUCCESS', 'success']);

export function emptySoftwareState(): SoftwareOtaState {
  return {
    components: [],
    solshareVersion: {
      reported: null,
      target: null,
      desired: null,
      minimum: null,
    },
  };
}

export function normalizeSoftwareState(raw: unknown): SoftwareOtaState {
  const data = (raw ?? {}) as Partial<SoftwareOtaState>;
  return {
    components: Array.isArray(data.components) ? data.components : [],
    solshareVersion: {
      reported: data.solshareVersion?.reported ?? null,
      target: data.solshareVersion?.target ?? null,
      desired: data.solshareVersion?.desired ?? null,
      minimum: data.solshareVersion?.minimum ?? null,
    },
  };
}

function componentStatusState(component: OtaComponent): string | null {
  const state = component.status?.state;
  return typeof state === 'string' && state.length > 0 ? state : null;
}

export function hasComponentError(components: OtaComponent[]): boolean {
  return components.some((component) => {
    const state = componentStatusState(component);
    return state !== null && ERROR_STATES.has(state);
  });
}

export function hasInProgressComponent(components: OtaComponent[]): boolean {
  return components.some((component) => {
    const state = componentStatusState(component);
    if (state === null) {
      return false;
    }
    if (ERROR_STATES.has(state) || TERMINAL_OK_STATES.has(state)) {
      return false;
    }
    return true;
  });
}

export function isHopInProgress(state: SoftwareOtaState): boolean {
  const { reported, target } = state.solshareVersion;
  const targetSet = typeof target === 'string' && target.length > 0;
  if (targetSet && reported !== target) {
    return true;
  }
  return hasInProgressComponent(state.components);
}

/**
 * Decide the next idle-loop action for one GET snapshot.
 * `intended` is usually solshareVersion.desired.
 */
export function decideSoftwareUpdateAction(
  state: SoftwareOtaState,
  intended: string,
): IdleLoopAction {
  if (!intended || typeof intended !== 'string') {
    return { type: 'fail', message: 'No intended SolShare software version' };
  }

  if (hasComponentError(state.components)) {
    return { type: 'fail', message: 'Software update failed on a component' };
  }

  const { reported } = state.solshareVersion;
  if (reported === intended) {
    return { type: 'done' };
  }

  if (isHopInProgress(state)) {
    return { type: 'poll' };
  }

  return { type: 'post', version: intended };
}

function coerceVersion(value: string | null | undefined): string | null {
  if (typeof value !== 'string' || !value.trim()) {
    return null;
  }
  const coerced = semver.coerce(value.trim());
  return coerced ? coerced.version : null;
}

/**
 * Compare product SBOM versions (e.g. v1.2.0 vs v3.2.3).
 * Returns true when reported is parseable and >= minimum.
 */
export function isReportedAtOrAboveMinimum(
  reported: string | null,
  minimum: string | null,
): boolean {
  const min = coerceVersion(minimum);
  if (!min) {
    return true;
  }
  const current = coerceVersion(reported);
  if (!current) {
    return false;
  }
  return semver.gte(current, min);
}

/**
 * Derive commissioning need/force from v3 GET solshareVersion only.
 * No fallback to legacy component-min flags.
 *
 * - needSoftwareUpdate: desired is set and reported !== desired
 * - forceUpdate: minimum is set and reported is null or < minimum
 */
export function deriveSoftwareUpdateRequirement(
  state: SoftwareOtaState,
): SoftwareUpdateRequirement {
  const { reported, desired, minimum } = state.solshareVersion;
  const desiredVersion = typeof desired === 'string' ? desired.trim() : '';
  const needSoftwareUpdate = Boolean(desiredVersion) && reported !== desiredVersion;
  const forceUpdate = !isReportedAtOrAboveMinimum(reported, minimum);

  return { needSoftwareUpdate, forceUpdate };
}
